import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from '../App';
import { useToast } from '../hooks/use-toast';

const ProviderBookings = () => {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingApproval, setProcessingApproval] = useState({});

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/provider/bookings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const bookingsData = await response.json();
        setBookings(bookingsData);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast({
        title: "Error",
        description: "Failed to load bookings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (bookingId, approved, rejectionReason = '') => {
    setProcessingApproval({...processingApproval, [bookingId]: true});
    
    try {
      const token = localStorage.getItem('dcTech_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/provider/bookings/${bookingId}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          approved: approved,
          rejectionReason: rejectionReason
        }),
      });

      if (response.ok) {
        toast({
          title: approved ? "Booking approved!" : "Booking rejected",
          description: approved 
            ? "Customer will receive a payment prompt to complete the booking."
            : "Customer has been notified of the rejection.",
        });
        
        // Refresh bookings
        fetchBookings();
      } else {
        throw new Error('Failed to update booking');
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      toast({
        title: "Error",
        description: "Failed to update booking. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessingApproval({...processingApproval, [bookingId]: false});
    }
  };

  const getStatusColor = (status, providerApproved) => {
    if (status === 'pending_approval') return 'bg-yellow-500';
    if (status === 'confirmed' && providerApproved) return 'bg-green-500';
    if (status === 'cancelled') return 'bg-red-500';
    if (status === 'completed') return 'bg-blue-500';
    return 'bg-gray-500';
  };

  const getStatusText = (booking) => {
    if (booking.status === 'pending_approval') return 'Awaiting Approval';
    if (booking.status === 'confirmed' && booking.paymentStatus === 'paid') return 'Confirmed & Paid';
    if (booking.status === 'confirmed' && booking.paymentRequested) return 'Payment Requested';
    if (booking.status === 'cancelled') return 'Cancelled';
    if (booking.status === 'completed') return 'Completed';
    return booking.status;
  };

  const pendingBookings = bookings.filter(b => b.status === 'pending_approval');
  const activeBookings = bookings.filter(b => b.status === 'confirmed');
  const completedBookings = bookings.filter(b => ['completed', 'cancelled'].includes(b.status));

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading bookings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-lg border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="text-3xl">⚡</div>
              <span className="text-2xl font-bold text-white">D.C. Technology Corp.</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link to="/provider-dashboard">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Provider Dashboard
                </Button>
              </Link>
              
              <Link to="/dashboard">
                <Button variant="outline" className="border-white/50 text-white bg-white/10">
                  Main Dashboard
                </Button>
              </Link>
              
              <Button 
                onClick={logout}
                variant="outline" 
                className="border-red-400/50 text-red-400 bg-red-500/20"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">Manage Bookings</h1>
          <p className="text-xl text-white/70">
            Review and approve service booking requests from customers
          </p>
        </div>

        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-slate-800/80 mb-8">
            <TabsTrigger value="pending" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              🕐 Pending Approval ({pendingBookings.length})
            </TabsTrigger>
            <TabsTrigger value="active" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              ✅ Active Bookings ({activeBookings.length})
            </TabsTrigger>
            <TabsTrigger value="completed" className="text-white data-[state=active]:bg-blue-600 data-[state=active]:text-white text-lg py-4">
              📋 Completed ({completedBookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <div className="space-y-6">
              {pendingBookings.length === 0 ? (
                <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                  <CardContent className="p-8 text-center">
                    <div className="text-6xl mb-6">🕐</div>
                    <h2 className="text-2xl font-bold text-white mb-4">No pending bookings</h2>
                    <p className="text-white/70">New booking requests will appear here for your approval.</p>
                  </CardContent>
                </Card>
              ) : (
                pendingBookings.map(booking => (
                  <Card key={booking._id} className="bg-white/10 backdrop-blur-lg border-white/20">
                    <CardHeader>
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-white text-xl">
                          New Booking Request
                        </CardTitle>
                        <Badge className="bg-yellow-500 text-white">
                          Pending Approval
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Customer Info */}
                        <div className="bg-white/5 rounded-lg p-4">
                          <h4 className="text-white font-semibold mb-3">Customer Details</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-white/70">Name:</span>
                              <span className="text-white">{booking.customer?.name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Email:</span>
                              <span className="text-white">{booking.customer?.email}</span>
                            </div>
                            {booking.customer?.phone && (
                              <div className="flex justify-between">
                                <span className="text-white/70">Phone:</span>
                                <span className="text-white">{booking.customer?.phone}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Service Details */}
                        <div className="bg-white/5 rounded-lg p-4">
                          <h4 className="text-white font-semibold mb-3">Service Details</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-white/70">Service:</span>
                              <span className="text-white">{booking.items[0]?.name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Type:</span>
                              <span className="text-white">{booking.serviceType}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Date:</span>
                              <span className="text-white">
                                {booking.scheduledDate ? new Date(booking.scheduledDate).toLocaleDateString() : 'Not set'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Time:</span>
                              <span className="text-white">{booking.scheduledTime}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-white/70">Total:</span>
                              <span className="text-green-400 font-bold">${booking.total}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Notes */}
                      {booking.notes && (
                        <div className="bg-white/5 rounded-lg p-4">
                          <h4 className="text-white font-semibold mb-2">Special Instructions</h4>
                          <p className="text-white/80">{booking.notes}</p>
                        </div>
                      )}

                      {/* Approval Actions */}
                      <div className="flex gap-4 pt-4">
                        <Button 
                          onClick={() => handleApproval(booking._id, true)}
                          disabled={processingApproval[booking._id]}
                          className="flex-1 bg-gradient-to-r from-green-500 to-blue-500   text-white"
                        >
                          {processingApproval[booking._id] ? "Approving..." : "✓ Approve & Request Payment"}
                        </Button>
                        <Button 
                          onClick={() => handleApproval(booking._id, false, 'Service not available for requested date/time')}
                          disabled={processingApproval[booking._id]}
                          variant="outline"
                          className="flex-1 border-red-400/50 text-red-400 bg-red-500/20"
                        >
                          {processingApproval[booking._id] ? "Processing..." : "✗ Reject"}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="active">
            <div className="space-y-4">
              {activeBookings.length === 0 ? (
                <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                  <CardContent className="p-8 text-center">
                    <div className="text-6xl mb-6">✅</div>
                    <h2 className="text-2xl font-bold text-white mb-4">No active bookings</h2>
                    <p className="text-white/70">Approved bookings will appear here.</p>
                  </CardContent>
                </Card>
              ) : (
                activeBookings.map(booking => (
                  <Card key={booking._id} className="bg-white/10 backdrop-blur-lg border-white/20">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-white font-semibold text-lg">{booking.items[0]?.name}</h3>
                        <Badge className={`${getStatusColor(booking.status, booking.providerApproved)} text-white`}>
                          {getStatusText(booking)}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-white/70">Customer:</span>
                          <div className="text-white">{booking.customer?.name}</div>
                        </div>
                        <div>
                          <span className="text-white/70">Type:</span>
                          <div className="text-white">{booking.serviceType}</div>
                        </div>
                        <div>
                          <span className="text-white/70">Date:</span>
                          <div className="text-white">
                            {booking.scheduledDate ? new Date(booking.scheduledDate).toLocaleDateString() : 'Not set'}
                          </div>
                        </div>
                        <div>
                          <span className="text-white/70">Total:</span>
                          <div className="text-green-400 font-semibold">${booking.total}</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="completed">
            <div className="space-y-4">
              {completedBookings.length === 0 ? (
                <Card className="bg-white/10 backdrop-blur-lg border-white/20">
                  <CardContent className="p-8 text-center">
                    <div className="text-6xl mb-6">📋</div>
                    <h2 className="text-2xl font-bold text-white mb-4">No completed bookings</h2>
                    <p className="text-white/70">Completed and cancelled bookings will appear here.</p>
                  </CardContent>
                </Card>
              ) : (
                completedBookings.map(booking => (
                  <Card key={booking._id} className="bg-white/10 backdrop-blur-lg border-white/20">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-white font-semibold text-lg">{booking.items[0]?.name}</h3>
                        <Badge className={`${getStatusColor(booking.status)} text-white`}>
                          {getStatusText(booking)}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-white/70">Customer:</span>
                          <div className="text-white">{booking.customer?.name}</div>
                        </div>
                        <div>
                          <span className="text-white/70">Date:</span>
                          <div className="text-white">
                            {booking.scheduledDate ? new Date(booking.scheduledDate).toLocaleDateString() : 'Not set'}
                          </div>
                        </div>
                        <div>
                          <span className="text-white/70">Total:</span>
                          <div className="text-green-400 font-semibold">${booking.total}</div>
                        </div>
                        <div>
                          <span className="text-white/70">Completed:</span>
                          <div className="text-white">
                            {new Date(booking.updatedAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProviderBookings;